import styled from 'styled-components';
import { colors } from '@/constants/colors';

/**
 * The Piso WiFi settings vocabulary, lifted verbatim so ISP settings look like
 * settings rather than a form somebody invented.
 *
 * Ray put the two side by side (2026-08-28): the panel's own settings are
 * sections — a small bold heading, a line of explanation, then a bordered card
 * of compact fields. Mine was a column of full-width inputs with a left-aligned
 * "Create my ISP business" button, which is neither the right density nor the
 * right verb for a settings screen.
 */

export const SectionTitle = styled.h3`
   font-size: 14px;
   font-weight: 700;
   color: ${colors.GRAY_DARK};
   margin: 20px 0 4px;
   display: flex;
   align-items: center;
   gap: 8px;
`;

export const SettingsNote = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
   line-height: 1.5;
   margin: 4px 0 12px;
`;

export const SettingsCard = styled.div<{ $grid?: boolean }>`
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
   padding: 16px;
   ${({ $grid }) =>
      $grid
         ? `display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 14px 16px;`
         : `display: flex;
            flex-direction: column;
            gap: 12px;`}
`;

export const SettingsField = styled.div<{ $wide?: boolean }>`
   display: flex;
   flex-direction: column;
   gap: 4px;
   ${({ $wide }) => $wide && 'grid-column: 1 / -1;'}
`;

export const SettingsLbl = styled.label`
   font-size: 11px;
   font-weight: 600;
   color: ${colors.GRAY3};
`;

export const SettingsInput = styled.input`
   padding: 8px 10px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   font-size: 13px;
   font-family: inherit;
   color: ${colors.MAIN_DARK};
   background: var(--app-surface);
   outline: none;
   &:focus {
      border-color: ${colors.PRIMARY};
   }
`;

export const SettingsHintSm = styled.div`
   font-size: 11px;
   color: ${colors.GRAY5};
   margin-top: 4px;
   line-height: 1.4;
`;

/** Actions sit bottom-RIGHT, as everywhere else in the panel. */
export const SettingsActions = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 10px;
   margin-top: 20px;
`;

export const SaveBtn = styled.button`
   padding: 9px 18px;
   border-radius: 8px;
   border: none;
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   font-size: 13px;
   font-weight: 600;
   cursor: pointer;

   &:disabled {
      opacity: 0.6;
      cursor: default;
   }
`;

/* Tab bar — lifted from the Piso WiFi panel so the two products' settings look
   like the same product's settings. */

export const TabBar = styled.div`
   display: flex;
   flex-wrap: nowrap;
   gap: 0;
   border-bottom: 1px solid ${colors.GRAY13};
   margin-bottom: 20px;
   overflow-x: auto;
   -webkit-overflow-scrolling: touch;
   scrollbar-width: none;
   &::-webkit-scrollbar {
      display: none;
   }
`;

export const TabBtn = styled.button<{ $active: boolean }>`
   display: flex;
   align-items: center;
   gap: 6px;
   flex: 0 0 auto;
   white-space: nowrap;
   background: none;
   border: none;
   padding: 10px 18px;
   font-size: 14px;
   font-weight: ${({ $active }) => ($active ? 700 : 500)};
   color: ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY3)};
   border-bottom: 2px solid
      ${({ $active }) => ($active ? colors.PRIMARY : 'transparent')};
   cursor: pointer;
   margin-bottom: -1px;
`;

/** A row in a settings list — a plan, a payment method. */
export const ListRow = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
   padding: 12px 0;
   border-bottom: 1px solid ${colors.GRAY13};

   &:last-child {
      border-bottom: none;
   }
`;

export const RowMain = styled.div`
   min-width: 0;
`;

export const RowTitle = styled.strong`
   display: block;
   font-size: 14px;
`;

export const RowSub = styled.span`
   display: block;
   font-size: 12px;
   color: ${colors.GRAY5};
   margin-top: 2px;
`;

/**
 * The buttons at the end of a table row — the ONE definition of them.
 *
 * Ray, 2026-09-02: *"action buttons always 'sagad sa right align' not only
 * this, i see other pages. also other action button has no space gap."* Four
 * pages had each grown their own copy, with gaps of 6px, 10px and none at all,
 * and none of them right-aligned. Import this; do not write another.
 *
 * 🔴 Right-aligned on purpose. The eye scans a row left to right and stops at
 * the action; ragged buttons make the operator hunt for it on every row. The
 * cell it sits in is right-aligned too — see `actionColumn` below — so the
 * header lines up with the buttons under it.
 */
export const ROW_ACTION_GAP = '8px';

export const RowActions = styled.div`
   display: flex;
   align-items: center;
   justify-content: flex-end;
   gap: ${ROW_ACTION_GAP};
   white-space: nowrap;
`;

/**
 * Drop into any table whose LAST column is its actions. Right-aligns the
 * header with them, so "Actions" is not stranded at the far left of a wide
 * column while its buttons sit at the other end.
 */
export const actionColumn = `
   th:last-child,
   td:last-child {
      text-align: right;
   }
`;

export const LinkBtn = styled.button`
   border: none;
   background: none;
   padding: 0;
   font-size: 13px;
   color: ${colors.PRIMARY};
   cursor: pointer;
`;

export const DangerLinkBtn = styled(LinkBtn)`
   color: ${colors.DANGER};
`;

export const AddBtn = styled.button`
   padding: 8px 14px;
   border-radius: 8px;
   border: 1px dashed ${colors.GRAY12};
   background: transparent;
   color: ${colors.PRIMARY};
   font-size: 13px;
   font-weight: 600;
   cursor: pointer;
`;

export const EmptyRow = styled.div`
   padding: 22px 0;
   text-align: center;
   font-size: 13px;
   color: ${colors.GRAY5};
`;
